import { useMemo, useState } from "react";
import type { CanonStatus, StoryEvent } from "../domain/types";
import { getMoment, sortedMoments } from "../domain/engine";
import { Field, Modal, SourceFields, Sources, StatusBadge, nowIso, submitGuard } from "../components/ui";
import { useStore } from "../store";

export function Timeline() {
  const { bible, selection, updateBible } = useStore();
  const [q, setQ] = useState("");
  const [importance, setImportance] = useState("");
  const [open, setOpen] = useState<StoryEvent | null>(null);
  const [adding, setAdding] = useState(false);
  if (!bible) return null;
  const moments = sortedMoments(bible, selection.orderMode);
  const events = useMemo(() => {
    return bible.events.filter((e) => {
      const m = getMoment(bible, e.momentId);
      if (selection.bookId && m && m.bookId !== selection.bookId) return false;
      if (selection.momentId) {
        const target = getMoment(bible, selection.momentId);
        if (target && e.branchId !== target.branchId && e.momentId !== target.id) {
          /* keep if same chapter display? filter to compatible later in grouping */
        }
      }
      if (importance && e.importance !== importance) return false;
      const hay = `${e.title} ${e.summary} ${e.characterIds.join(" ")}`.toLowerCase();
      return hay.includes(q.toLowerCase());
    });
  }, [bible, importance, q, selection.bookId, selection.momentId]);

  const grouped = moments
    .filter((m) => !selection.bookId || m.bookId === selection.bookId)
    .filter((m) => !selection.momentId || m.id === selection.momentId || (getMoment(bible, selection.momentId)?.chapterNumber === m.chapterNumber && m.branchId === getMoment(bible, selection.momentId)?.branchId))
    .map((m) => ({ moment: m, events: events.filter((e) => e.momentId === m.id) }))
    .filter((g) => g.events.length || !q);

  return (
    <div>
      <div className="row space">
        <h1>Chronologie</h1>
        <button className="btn primary" type="button" onClick={() => setAdding(true)} disabled={!selection.momentId}>
          Ajouter un événement
        </button>
      </div>
      <p className="tiny">
        Ordre actuel : {selection.orderMode === "reading" ? "lecture" : "interne"}. Le chapitre 0 du Tome 1 change de place selon ce mode, sans modifier les identifiants.
      </p>
      <div className="row">
        <input className="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…" />
        <select value={importance} onChange={(e) => setImportance(e.target.value)}>
          <option value="">Importance</option>
          <option value="major">majeur</option>
          <option value="supporting">soutien</option>
          <option value="minor">mineur</option>
        </select>
      </div>
      <div className="timeline">
        {grouped.map(({ moment, events: evs }) => (
          <div key={moment.id} className="tl-item">
            <div>
              <strong>{moment.label}</strong>
              <div className="tiny">{moment.title} · {moment.branchId}</div>
            </div>
            <div className="tl-rail"><span /></div>
            <div className="list">
              {evs.map((e) => (
                <button key={e.id} className="item" type="button" onClick={() => setOpen(e)}>
                  <div className="row space">
                    <strong>{e.title}</strong>
                    <StatusBadge status={e.status} />
                  </div>
                  <div className="tiny">{e.summary}</div>
                </button>
              ))}
              {!evs.length && <div className="tiny">Pas d’événement structuré.</div>}
            </div>
          </div>
        ))}
      </div>
      <Modal open={!!open} title={open?.title ?? ""} onClose={() => setOpen(null)}>
        {open && (
          <>
            <p>{open.summary}</p>
            <p className="tiny">Moment {open.momentId} · branche {open.branchId}</p>
            {open.consequences?.length ? <p>Conséquences : {open.consequences.join(" ; ")}</p> : null}
            <Sources sources={open.sources} />
          </>
        )}
      </Modal>
      <EventForm
        open={adding}
        onClose={() => setAdding(false)}
        defaultMomentId={selection.momentId}
        defaultBranchId={getMoment(bible, selection.momentId)?.branchId ?? "branch:main"}
        characters={bible.characters.map((c) => ({ id: c.id, name: c.name }))}
        onCreate={(event) => {
          updateBible((draft) => ({ ...draft, events: [...draft.events, event] }), `Ajout événement ${event.title}`);
          setAdding(false);
        }}
      />
    </div>
  );
}

function EventForm({
  open,
  onClose,
  defaultMomentId,
  defaultBranchId,
  characters,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  defaultMomentId: string | null;
  defaultBranchId: string;
  characters: Array<{ id: string; name: string }>;
  onCreate: (e: StoryEvent) => void;
}) {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState<CanonStatus>("draft");
  const [sourceLabel, setSourceLabel] = useState("");
  const [who, setWho] = useState<string[]>([]);
  return (
    <Modal open={open} title="Nouvel événement" onClose={onClose}>
      <form
        className="grid"
        onSubmit={(e) => {
          if (!submitGuard(e, status, sourceLabel) || !defaultMomentId) return;
          const id = `event:${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}-${Date.now().toString(36)}`;
          onCreate({
            id,
            status,
            sources: sourceLabel ? [{ label: sourceLabel }] : [],
            createdAt: nowIso(),
            updatedAt: nowIso(),
            momentId: defaultMomentId,
            title,
            summary,
            importance: "supporting",
            characterIds: who,
            branchId: defaultBranchId,
          });
        }}
      >
        <p className="tiny">Le statut restera {status}. Le canon n’est jamais mélangé silencieusement.</p>
        <Field label="Titre"><input value={title} onChange={(e) => setTitle(e.target.value)} required /></Field>
        <Field label="Résumé"><textarea value={summary} onChange={(e) => setSummary(e.target.value)} required rows={4} /></Field>
        <Field label="Personnages">
          <select multiple value={who} onChange={(e) => setWho([...e.target.selectedOptions].map((o) => o.value))} style={{ minHeight: 120 }}>
            {characters.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </Field>
        <SourceFields status={status} sourceLabel={sourceLabel} setStatus={setStatus} setSourceLabel={setSourceLabel} />
        <button className="btn primary" type="submit" disabled={!defaultMomentId}>Enregistrer le brouillon</button>
      </form>
    </Modal>
  );
}
